import React, { useCallback, useContext, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Pressable,
} from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { GlobalStateContext } from '../../components/general/GlobalStateProvider';
import CreditCardManager from './components/CreditCardManager';
import DataOps from '../../data/DataOps';

const PaymentMethodPage: React.FC = () => {
  const { padding, user } = useContext(GlobalStateContext);
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);
  const [, setPaymentMethods] = useState<any[]>([]);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [historyError, setHistoryError] = useState('');

  const handleMethodsChange = useCallback((methods: any[]) => {
    setPaymentMethods(methods || []);
  }, []);

  const toMillis = (value: any): number => {
    if (!value) return 0;
    if (typeof value?.toDate === 'function') return value.toDate().getTime();
    if (typeof value?.seconds === 'number') return (value.seconds * 1000) + Math.floor((value.nanoseconds || 0) / 1e6);
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const formatDate = (value: any): string => {
    const millis = toMillis(value);
    if (!millis) return 'Unknown date';
    return new Date(millis).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const toAmount = (tx: any, session: any): number | null => {
    const raw = tx?.amount ?? tx?.totalAmount ?? tx?.price ?? session?.price;
    const num = typeof raw === 'number' ? raw : parseFloat(String(raw || ''));
    return Number.isFinite(num) ? num : null;
  };

  const formatAmount = (value: number | null): string => {
    if (value === null) return '--';
    return `$${value.toFixed(2)}`;
  };

  const loadTransactions = async () => {
    if (!user?.userId) return;

    setHistoryLoading(true);
    setHistoryError('');
    try {
      const tx = await DataOps.fetchSessionTransactionsByUser(user.userId, 25);
      const sessionIds = Array.from(new Set((tx || []).map((item: any) => item?.sessionId).filter(Boolean)));
      const sessionEntries = await Promise.all(
        sessionIds.map(async (sessionId: string) => {
          const session = await DataOps.fetchDataFromFirebase('session', sessionId, true);
          return [sessionId, session] as const;
        })
      );
      const sessionMap = new Map<string, any>(sessionEntries);

      const history = (tx || []).map((item: any) => {
        const session = sessionMap.get(item.sessionId) || {};
        const child = user?.children?.find((c: any) => c.childId === item.childId);
        const childName = child ? `${child.firstName || ''} ${child.lastName || ''}`.trim() : '';
        const statusLabel = item?.status === 'unregistered' ? 'Unregistered' : 'Registered';
        const sessionTitle = session?.title || 'Camp session';
        const childSuffix = childName ? ` • ${childName}` : '';

        return {
          id: item.transactionId || `${item.sessionId}_${item.childId}_${toMillis(item.createdAt)}`,
          date: item.createdAt || item.updatedAt,
          amount: toAmount(item, session),
          description: `${statusLabel}: ${sessionTitle}${childSuffix}`,
        };
      });

      setTransactions(history);
      setHistoryLoaded(true);
    } catch (error) {
      setHistoryError('Unable to load transaction history right now.');
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <ScrollView contentContainerStyle={[styles.container, {paddingRight: padding, paddingLeft: padding + 50}]}>
      <View style={styles.paymentContainer}>
        <View style={styles.iconContainer}>
          <MaterialCommunityIcons name="credit-card-outline" size={80} color="#222" />
        </View>

        <View style={styles.paymentTitleContainer}>
          <Text style={styles.paymentTitleText}>
            Payments
          </Text>
        </View>

        <Text style={styles.description}>
          Manage your payment methods for SnapCamp purchases and bookings.
          <Text style={styles.link}> Learn more</Text>
        </Text>

        <View style={styles.paymentMethodsContainer}>
          {/* Credit Card Manager — always visible */}
          <View style={styles.sectionHeader}>
            <MaterialCommunityIcons
              name="credit-card-outline"
              size={22}
              color="#333"
              style={{ marginRight: 8 }}
            />
            <Text style={styles.sectionTitle}>Payment Methods</Text>
          </View>
          <View style={styles.sectionContent}>
            <CreditCardManager onMethodsChange={handleMethodsChange} />
          </View>



          {/* Payment History */}
          <Pressable
            style={styles.sectionHeader}
            onPress={() => {
              const nextExpanded = !isHistoryExpanded;
              setIsHistoryExpanded(nextExpanded);
              if (nextExpanded && !historyLoaded && !historyLoading) {
                loadTransactions();
              }
            }}
          >
            <MaterialCommunityIcons name="history" size={22} color="#333" style={{ marginRight: 8 }} />
            <Text style={[styles.sectionTitle, { flex: 1 }]}>Payment History</Text>
            <MaterialCommunityIcons
              name={isHistoryExpanded ? 'chevron-up' : 'chevron-down'}
              size={20}
              color="#666"
            />
          </Pressable>
          {isHistoryExpanded && (
            <View style={styles.sectionContent}>
              <View style={styles.billingDetails}>
                {historyLoading && (
                  <Text style={styles.detailSubtext}>Loading transaction history...</Text>
                )}
                {!historyLoading && !!historyError && (
                  <Text style={styles.detailSubtext}>{historyError}</Text>
                )}
                {!historyLoading && !historyError && transactions.length === 0 && (
                  <Text style={styles.detailSubtext}>No transaction history yet.</Text>
                )}
                {!historyLoading && !historyError && transactions.map((tx) => (
                  <View key={tx.id} style={styles.transactionItem}>
                    <Text style={styles.transactionDate}>{formatDate(tx.date)}</Text>
                    <Text style={styles.transactionAmount}>{formatAmount(tx.amount)}</Text>
                    <Text style={styles.transactionDescription}>{tx.description}</Text>
                  </View>
                ))}
              </View>
            </View>
          )}

        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingTop: 20,
    backgroundColor: '#fff',
    flexGrow: 1,
  },
  paymentContainer: {
    alignItems: 'center',
    marginBottom: 32,
  },
  iconContainer: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: '#f8f8f8',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
    borderWidth: 2,
    borderColor: '#e0e0e0',
  },
  paymentTitleContainer: {
    marginBottom: 16,
    paddingTop: 8,
    paddingBottom: 8,
  },
  paymentTitleText: {
    fontSize: 24,
    fontWeight: '700',
    textAlign: 'center',
  },
  paymentTitleInput: {
    fontSize: 24,
    fontWeight: '700',
    textAlign: 'center',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#f9f9f9',
  },
  description: {
    fontSize: 14,
    color: '#555',
    textAlign: 'center',
    marginHorizontal: 24,
    marginBottom: 24,
  },
  link: {
    color: '#008489',
  },
  paymentMethodsContainer: {
    width: '100%',
    gap: 24,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: '#1E293B',
  },
  sectionContent: {
    width: '100%',
  },
  billingDetails: {
    padding: 16,
    backgroundColor: '#f8f8f8',
    borderRadius: 8,
  },
  detailSubtext: {
    fontSize: 12,
    color: '#666',
    lineHeight: 16,
  },
  transactionItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  transactionDate: {
    fontSize: 12,
    color: '#666',
    width: 80,
  },
  transactionAmount: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    width: 60,
    textAlign: 'right',
  },
  transactionDescription: {
    fontSize: 12,
    color: '#333',
    flex: 1,
    marginLeft: 12,
  },
  addressSection: {
    marginBottom: 24,
    paddingHorizontal: 16,
  },
  addressRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  addressIcon: {
    marginRight: 12,
    color: '#333',
  },
  streetAddressContainer: {
    flex: 1,
  },
  streetAddressInput: {
    fontSize: 14,
    color: '#333',
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 6,
    paddingHorizontal: 12,
    backgroundColor: '#f9f9f9',
    marginBottom: 4,
  },
  displayText: {
    fontSize: 14,
    color: '#333',
  },
  cityStateZipContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  cityStateZipInputs: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  cityStateZipInput: {
    fontSize: 14,
    color: '#333',
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 6,
    paddingHorizontal: 12,
    backgroundColor: '#f9f9f9',
  },
  cityInput: {
    width: 100,
    marginRight: 8,
  },
  stateInput: {
    width: 80,
    marginRight: 8,
  },
  zipInput: {
    width: 80,
  },
});

export default PaymentMethodPage; 